import { type SyntheticEvent, useEffect, useId, useRef, useState } from "react";

import { DropdownMenu as DropdownMenuUI } from "../dropdown-menu";

import { DropdownMenuContent } from "./DropdownMenuContent";
import { DropdownMenuTrigger } from "./DropdownMenuTrigger";
import { DropdownMenuProps } from "./types";

const OPEN_DROPDOWN_EVENT = "promptlayer:data-table-dropdown-open";
const CLOSE_ALL_DROPDOWNS_EVENT = "promptlayer:data-table-dropdown-close-all";

export const closeAllDataTableDropdownMenus = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(CLOSE_ALL_DROPDOWNS_EVENT));
};

export const DropdownMenu = <DataT,>({
  menuItems,
  icon,
  label,
  item,
  triggerClassName,
  contentClassName,
  isOnHover,
  onTriggerClick,
}: DropdownMenuProps<DataT>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownId = useId();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (isOnHover) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (isOnHover) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 150);
    }
  };

  useEffect(() => {
    const handleOtherDropdownOpen = (event: Event) => {
      if (!(event instanceof CustomEvent) || event.detail === dropdownId) {
        return;
      }

      setIsOpen(false);
    };

    const handleCloseAllDropdowns = () => {
      setIsOpen(false);
    };

    window.addEventListener(OPEN_DROPDOWN_EVENT, handleOtherDropdownOpen);
    window.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAllDropdowns);

    return () => {
      window.removeEventListener(OPEN_DROPDOWN_EVENT, handleOtherDropdownOpen);
      window.removeEventListener(
        CLOSE_ALL_DROPDOWNS_EVENT,
        handleCloseAllDropdowns,
      );
    };
  }, [dropdownId]);

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (nextIsOpen) {
      window.dispatchEvent(
        new CustomEvent(OPEN_DROPDOWN_EVENT, {
          detail: dropdownId,
        }),
      );
    }

    setIsOpen(nextIsOpen);
  };

  const handleTriggerClick = () => {
    onTriggerClick?.(item);
  };

  const stopTriggerPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <DropdownMenuUI
      open={isOpen}
      onOpenChange={handleOpenChange}
      modal={!isOnHover}
    >
      <div
        className="flex h-full items-center justify-center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onPointerDown={stopTriggerPropagation}
        onMouseDown={stopTriggerPropagation}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <DropdownMenuTrigger
          icon={icon}
          onTriggerClick={handleTriggerClick}
          label={label}
          className={triggerClassName}
        />
        <DropdownMenuContent
          menuItems={menuItems}
          item={item}
          className={contentClassName}
        />
      </div>
    </DropdownMenuUI>
  );
};

export { DropdownMenuVariant } from "./types";
export type { Action, DropdownMenuProps } from "./types";
